/**
 * Login Page
 * 
 * @validates Requirements 3.1, 3.4 - Login with generic error
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Button, Input, Card } from '../../components/ui';
import { TwoFactorForm, TwoFactorResult } from '../../components/auth/TwoFactorForm';
import GoogleLoginButton from '../../components/auth/GoogleLoginButton';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    login, 
    isLoading, 
    error,
    requiresTwoFactor, 
    requiresEmail2FA,
    userId,
    email2FASessionId,
    verifyEmail2FA,
    resendEmail2FA,
    clearError,
    resetEmail2FAState,
  } = useAuthStore();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  // Check if redirected due to session expiration or Google error
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setSessionExpired(true);
      // Remove the expired param from URL
      searchParams.delete('expired');
      setSearchParams(searchParams, { replace: true });
    }
    
    // Check for Google OAuth errors
    const googleErrorParam = searchParams.get('error');
    if (googleErrorParam) {
      setGoogleError(googleErrorParam);
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    const success = await login(email, password, rememberMe);
    if (success) {
      if (requiresTwoFactor) {
        navigate('/auth/2fa');
      } else if (!requiresEmail2FA) {
        // Only navigate to dashboard if no 2FA is required
        navigate('/dashboard');
      }
      // If requiresEmail2FA is true, we stay on this page and show the 2FA form
    }
  };

  const handleEmail2FASubmit = useCallback(
    async (_userId: string, _sessionId: string, code: string): Promise<TwoFactorResult> => {
      const result = await verifyEmail2FA(code);
      return result;
    },
    [verifyEmail2FA]
  );

  const handleEmail2FAResend = useCallback(
    async (_userId: string, _sessionId: string): Promise<{ success: boolean; error?: string }> => {
      return await resendEmail2FA();
    },
    [resendEmail2FA]
  );

  const handleEmail2FASuccess = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleBack = useCallback(() => {
    resetEmail2FAState();
  }, [resetEmail2FAState]);

  // Show 2FA form if email 2FA is required
  if (requiresEmail2FA && userId && email2FASessionId) {
    return (
      <Card>
        <TwoFactorForm
          userId={userId}
          sessionId={email2FASessionId}
          onSubmit={handleEmail2FASubmit}
          onResend={handleEmail2FAResend}
          onSuccess={handleEmail2FASuccess}
          onBack={handleBack}
          codeValiditySeconds={300}
          maxAttempts={3}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('auth.login.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">{t('auth.login.subtitle')}</p>
      </div>

      {sessionExpired && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg text-sm text-amber-700 dark:text-amber-400">
          {t('auth.login.sessionExpired', 'Votre session a expiré. Veuillez vous reconnecter.')}
        </div>
      )}

      {googleError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-600 dark:text-red-400">
          {t(`auth.google.errors.${googleError}`, t('auth.google.errors.generic', 'Erreur lors de la connexion avec Google'))}
        </div>
      )}

      {/* Google Login Button */}
      <GoogleLoginButton className="mb-4" />

      {/* Divider */}
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400">
            {t('auth.login.orWithEmail', 'ou avec votre email')}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('auth.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          required
          autoComplete="email"
        />

        <Input
          label={t('auth.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-violet-500 focus:ring-violet-500 dark:bg-slate-700"
            />
            <span className="text-sm text-slate-600 dark:text-slate-400">{t('auth.login.rememberMe')}</span>
          </label>
          <Link 
            to="/auth/forgot-password" 
            className="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium"
          >
            {t('auth.login.forgotPassword')}
          </Link>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error.toLowerCase().includes('maintenance')
              ? t('auth.login.errors.maintenanceMode')
              : t('auth.login.errors.invalidCredentials')}
          </div>
        )}

        <Button type="submit" className="w-full" isLoading={isLoading}>
          {t('auth.login.submit')}
        </Button>
      </form>

      <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        {t('auth.login.noAccount')}{' '}
        <Link to="/auth/register" className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium">
          {t('auth.login.register')}
        </Link>
      </div>
    </Card>
  );
};

export default LoginPage;
